import { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { track } from '../lib/telemetry';

/**
 * iter-221c / iter-231B — Vendor Trial Gate
 *
 * Wraps vendor dashboard routes. On mount, asks the backend for the live
 * trial-gate verdict (`/api/vendor-subscription/trial-gate-status`) and:
 *
 *   • gate === 'ok'            → renders children
 *   • gate === 'trial_expired' → redirects to /vendor/subscription-hub
 *   • gate === 'suspended'     → redirects with a stronger banner
 *
 * The verdict cache is MODULE-LEVEL (not per-component) so a remount of
 * TrialGate triggered by Outer route changes does not re-fetch.
 *
 * iter-231B critical fix: while the gate response is in flight we render
 * `null` (NOT children). Previously we rendered children while loading,
 * which mounted the entire vendor dashboard tree and let it fire
 * `vendor-notifications`, `dashboard/stats`, `suggestions`, etc. before
 * the gate verdict arrived. Trial-expired vendors then saw their
 * dashboard briefly render + flood the backend with 429s while the
 * redirect kicked in. Holding on null avoids ALL of that.
 *
 * Source of truth: only `verdict.gate === 'trial_expired'` (or 'suspended')
 * triggers the redirect. `days_overdue`, `trial_end < now`, and any
 * client-side date arithmetic are NEVER used to derive expiry.
 */
const GATE_CACHE_TTL_MS = 5 * 60 * 1000;
const ALLOWED_PATHS = [
  '/vendor/subscription-hub',
  '/vendor/billing',
  '/vendor/store-profile',
  '/profile',
  '/change-password',
  '/logout',
];

// Module-level cache. Shared across every TrialGate mount/remount in this
// browser tab. Keyed by the current user id so a user-switch invalidates.
const moduleGateCache = {
  vendorId: null,
  verdict: null,
  fetchedAt: 0,
  inFlight: null,
};

// Module-level idempotency set for telemetry firing. See iter-231B.3
// comment in the render body — we don't want to fire `trial_gate_hit`
// multiple times for the same (gate, path) tuple even across remounts.
const TELEMETRY_FIRED_SESSIONS = new Set();

function isGatedVerdict(v) {
  return v && (v.gate === 'trial_expired' || v.gate === 'suspended');
}

export default function TrialGate({ children }) {
  const { user, apiCall } = useAuth();
  const location = useLocation();

  // Only gate vendors. Admins / customers / pending-onboarding flow through.
  const shouldGate =
    user &&
    user.role === 'vendor' &&
    user.onboarding_completed === true &&
    !ALLOWED_PATHS.some((p) => location.pathname.startsWith(p));

  // iter-231B.3 — Optimistic initial state to eliminate the
  // blank-screen flicker. Pre-iter-231B we returned `children` while
  // loading, which let trial-expired dashboards briefly render. Now we
  // start with the cached verdict if it exists (TTL is enforced inside
  // useEffect, not in the initializer — `Date.now()` in a useState
  // initializer violates react-hooks/purity). Only the first-ever
  // fetch shows a (very brief) null state.
  const hasCachedVerdict =
    shouldGate && moduleGateCache.verdict && moduleGateCache.vendorId === user?.id;
  const [verdict, setVerdict] = useState(() =>
    hasCachedVerdict ? moduleGateCache.verdict : (shouldGate ? null : { gate: 'ok' })
  );
  const [loading, setLoading] = useState(() => shouldGate && !hasCachedVerdict);

  useEffect(() => {
    let cancelled = false;
    if (!shouldGate) {
      // Already set via optimistic initializer; defensively keep in
      // sync when shouldGate flips false at runtime (e.g. user signs
      // out or navigates into ALLOWED_PATHS while we held a stale
      // verdict). The set-state-in-effect lint rule is a false
      // positive here — this IS the canonical place to sync state to
      // a derived condition that changes over time.
      // eslint-disable-next-line
      setVerdict({ gate: 'ok' });
      setLoading(false);
      return;
    }

    // User changed → invalidate the module cache.
    if (moduleGateCache.vendorId && moduleGateCache.vendorId !== user.id) {
      moduleGateCache.vendorId = null;
      moduleGateCache.verdict = null;
      moduleGateCache.fetchedAt = 0;
      moduleGateCache.inFlight = null;
    }

    // Fresh cached verdict → use immediately.
    if (
      moduleGateCache.verdict &&
      moduleGateCache.vendorId === user.id &&
      Date.now() - moduleGateCache.fetchedAt < GATE_CACHE_TTL_MS
    ) {
      setVerdict(moduleGateCache.verdict);
      setLoading(false);
      return;
    }

    // De-duplicate concurrent calls: reuse an in-flight promise if one
    // exists. Prevents 3-5 simultaneous `trial-gate-status` calls when
    // multiple TrialGate-wrapped routes mount at the same time.
    if (!moduleGateCache.inFlight) {
      moduleGateCache.inFlight = (async () => {
        try {
          const data = await apiCall('/api/vendor-subscription/trial-gate-status');
          moduleGateCache.vendorId = user.id;
          moduleGateCache.verdict = data;
          moduleGateCache.fetchedAt = Date.now();
          return data;
        } catch (e) {
          // Fail-open: if the gate API is unreachable (network drop,
          // 429, 502, FORBIDDEN_BACKOFF, RATE_LIMITED, etc.) don't
          // hold the vendor hostage — return ok. Do NOT cache it so
          // the next render attempts a real lookup.
          return { gate: 'ok', _fallback: true, _error: e?.code || e?.message };
        } finally {
          moduleGateCache.inFlight = null;
        }
      })();
    }

    moduleGateCache.inFlight.then((data) => {
      if (cancelled) return;
      setVerdict(data);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [shouldGate, user?.id, location.pathname, apiCall]);

  // iter-231B critical change: render `null` while the verdict is in
  // flight (not children). Children mounting prematurely is what caused
  // the trial-expired-vendor dashboard to briefly render + spam APIs.
  if (loading) return null;

  // SOURCE OF TRUTH: only `verdict.gate` decides expiry. Never
  // `days_overdue`, never `trial_end < now`.
  if (!isGatedVerdict(verdict)) return children;

  // iter-231B.3 — DEFENSIVE: if we're already on the redirect target,
  // don't re-fire `<Navigate>` (every render of <Navigate> calls
  // history.replaceState, and Chrome throttles after ~50 navigations
  // within 10s, freezing the tab). The catch-all + alias route fix
  // also addresses the underlying cause, but this guard is the second
  // line of defense against any future redirect-target typo. We just
  // hold the user on the destination component (children rendered
  // by the wrapping Route) instead of redirecting again.
  const targetPath = '/vendor/subscription-hub';
  if (location.pathname === targetPath) return children;

  // Fire funnel telemetry once per redirect (not on every re-render).
  // iter-231B.3 — Use a module-level Set (not a per-component useRef
  // mutated during render) so the firing is naturally idempotent
  // across re-renders + remounts. Setting `ref.current` during render
  // trips the react-hooks/refs lint and is a soft React anti-pattern.
  if (verdict && !TELEMETRY_FIRED_SESSIONS.has(verdict.gate + '|' + location.pathname)) {
    TELEMETRY_FIRED_SESSIONS.add(verdict.gate + '|' + location.pathname);
    try {
      track('trial_gate_hit', {
        gate: verdict.gate,
        reason: verdict.reason,
        days_overdue: verdict.days_overdue,
        from_path: location.pathname,
      });
    } catch (_) {
      // Telemetry must never block the redirect.
    }
  }

  const target = `${targetPath}?gate=${verdict.gate}&reason=${verdict.reason || ''}`;
  return <Navigate to={target} replace state={{ trialGateVerdict: verdict }} />;
}
